import { createContext, useContext, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { UserRole, ADMIN_GROUPS, DEVELOPER_GROUPS } from './types';
import { useUserIdentity } from './useUserIdentity';

const STORAGE_KEY = 'backstage.activeRole';

interface RoleContextValue {
  activeRole: UserRole | null;
  setActiveRole: (role: UserRole) => void;
  isAdmin: boolean;
  isPlatformLead: boolean;
  isDeveloper: boolean;
  loading: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  activeRole: null,
  setActiveRole: () => {},
  isAdmin: false,
  isPlatformLead: false,
  isDeveloper: false,
  loading: true,
});

export function RoleProvider({ children }: PropsWithChildren<{}>) {
  const { identity, loading: identityLoading } = useUserIdentity();

  const [activeRole, setActiveRoleState] = useState<UserRole | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as UserRole;
      }
    } catch {
      // ignore
    }
    return null;
  });

  useEffect(() => {
    if (identity && !activeRole) {
      setActiveRoleState(identity.elevatedRole);
    }
  }, [identity, activeRole]);

  const setActiveRole = useCallback((role: UserRole) => {
    setActiveRoleState(role);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(role));
    } catch {
      // ignore
    }
  }, []);

  const isAdmin = ADMIN_GROUPS.includes(activeRole?.groupName ?? '');
  const isPlatformLead = activeRole?.groupName === 'platform-lead';
  const isDeveloper = isAdmin || DEVELOPER_GROUPS.includes(activeRole?.groupName ?? '');

  return (
    <RoleContext.Provider
      value={{
        activeRole,
        setActiveRole,
        isAdmin,
        isPlatformLead,
        isDeveloper,
        loading: identityLoading,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRoleContext() {
  return useContext(RoleContext);
}
